if (!customElements.get('material-hero-v2')) {
  class MaterialHeroV2 extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.intervalMs = Math.max(2000, (parseInt(this.dataset.interval, 10) || 2) * 1000);
      this.style.setProperty('--mhv2-interval', `${this.intervalMs}ms`);

      this.mediaSlides = [...this.querySelectorAll('.mhv2__media-slide')];
      this.progressDots = [...this.querySelectorAll('.mhv2__progress-dot')];
      this.trackFill = this.querySelector('.mhv2__progress-track-fill');
      this.count = this.mediaSlides.length;

      if (this.count < 1) return;

      this.index = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.isPaused = false;
      this.isVisible = !('IntersectionObserver' in window);

      this.progressDots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const i = parseInt(dot.dataset.slideIndex, 10);
          if (!Number.isNaN(i)) this.goTo(i, true);
        });
      });

      if (this.trackFill) {
        this._onProgressEnd = (event) => {
          if (event.target !== this.trackFill || event.animationName !== 'mhv2-progress-to') return;
          if (this.isPaused || this.reducedMotion || !this.isVisible) return;
          this.next();
        };
        this.trackFill.addEventListener('animationend', this._onProgressEnd);
      }

      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.resume();
      });

      this._onVisibilityChange = () => {
        if (document.hidden) this.pause();
        else this.resume();
      };
      document.addEventListener('visibilitychange', this._onVisibilityChange);

      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;
            this._setVisible(entry.isIntersecting);
          },
          { threshold: 0.2 }
        );
        this._observer.observe(this);
      }

      this.syncProgressDots();

      if (this.count >= 2) {
        this.snapTrack(this.progressPct(this.index));
        this._startAutoplayIfVisible();
      } else {
        this.setTrackProgress(this.index, false);
      }
    }

    _startAutoplayIfVisible() {
      if (!('IntersectionObserver' in window)) {
        this._setVisible(true);
        return;
      }

      const pending = this._observer?.takeRecords();
      if (pending?.length) {
        this._setVisible(pending[pending.length - 1].isIntersecting);
        if (this.isVisible) return;
      }

      requestAnimationFrame(() => {
        if (!this.isVisible) this._setVisible(this._isInViewport());
      });
    }

    _isInViewport() {
      const rect = this.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return false;

      const vh = window.innerHeight || document.documentElement.clientHeight;
      const visibleHeight = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
      if (visibleHeight <= 0) return false;

      return visibleHeight / rect.height >= 0.2;
    }

    _setVisible(visible) {
      if (visible === this.isVisible) {
        if (visible && this.isPaused && this.count >= 2) this.play();
        return;
      }

      this.isVisible = visible;
      if (visible) this.play();
      else this.pause();
    }

    disconnectedCallback() {
      this.pause();
      this.clearFallbackTimer();
      if (this._observer) this._observer.disconnect();
      if (this._onVisibilityChange) {
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
      }
      if (this.trackFill && this._onProgressEnd) {
        this.trackFill.removeEventListener('animationend', this._onProgressEnd);
      }
    }

    progressPct(slideIndex) {
      if (this.count <= 1) return 0;
      return (slideIndex / (this.count - 1)) * 100;
    }

    goTo(index, userTriggered = false) {
      if (index < 0 || index >= this.count || index === this.index) return;

      const prev = this.index;

      this.cancelProgressAnimation();

      this.mediaSlides[prev]?.classList.remove('is-active');
      this.mediaSlides[prev]?.setAttribute('aria-hidden', 'true');
      this.progressDots[prev]?.classList.remove('is-active');
      this.progressDots[prev]?.setAttribute('aria-selected', 'false');

      this.index = index;

      this.mediaSlides[this.index]?.classList.add('is-active');
      this.mediaSlides[this.index]?.setAttribute('aria-hidden', 'false');
      this.progressDots[this.index]?.setAttribute('aria-selected', 'true');

      this.syncProgressDots();

      if (userTriggered) {
        this.snapTrack(this.progressPct(this.index));
        this.pause();
        this.play();
        return;
      }

      if (!this.reducedMotion && !this.isPaused && this.isVisible) {
        this.runProgressAnimation();
      } else {
        this.snapTrack(this.progressPct(this.index));
        this.scheduleFallbackAdvance();
      }
    }

    next() {
      const nextIndex = (this.index + 1) % this.count;
      if (nextIndex === 0 && this.trackFill) {
        this.snapTrack(0);
      }
      this.goTo(nextIndex);
    }

    syncProgressDots() {
      this.progressDots.forEach((dot, i) => {
        dot.classList.toggle('is-complete', i < this.index);
        dot.classList.toggle('is-active', i === this.index);
      });
    }

    cancelProgressAnimation() {
      if (!this.trackFill) return;
      this.trackFill.classList.remove('is-animating');
      this.trackFill.style.animationPlayState = '';
    }

    snapTrack(pct) {
      if (!this.trackFill) return;
      this.cancelProgressAnimation();
      this.trackFill.style.width = `${pct}%`;
    }

    setTrackProgress(slideIndex, animate = true) {
      if (!this.trackFill) return;

      const at = this.progressPct(slideIndex);

      if (!animate || this.reducedMotion) {
        this.snapTrack(at);
        return;
      }

      this.runProgressAnimation(slideIndex);
    }

    runProgressAnimation(slideIndex = this.index) {
      if (!this.trackFill || this.reducedMotion) return;

      const at = this.progressPct(slideIndex);
      const target = slideIndex < this.count - 1 ? this.progressPct(slideIndex + 1) : 100;

      this.cancelProgressAnimation();
      this.trackFill.style.width = `${at}%`;
      this.trackFill.style.setProperty('--mhv2-progress-from', `${at}%`);
      this.trackFill.style.setProperty('--mhv2-progress-to', `${target}%`);
      void this.trackFill.offsetWidth;
      this.trackFill.style.animationPlayState = 'running';
      this.trackFill.classList.add('is-animating');
      this.clearFallbackTimer();
    }

    clearFallbackTimer() {
      if (this._fallbackTimer) {
        clearTimeout(this._fallbackTimer);
        this._fallbackTimer = null;
      }
    }

    scheduleFallbackAdvance() {
      this.clearFallbackTimer();
      if (this.reducedMotion && !this.isPaused && this.isVisible && this.count >= 2) {
        this._fallbackTimer = window.setTimeout(() => {
          if (!this.isPaused && this.isVisible) this.next();
        }, this.intervalMs);
      }
    }

    play() {
      if (this.count < 2) return;
      this.isPaused = false;
      if (!this.isVisible) return;

      if (this.reducedMotion) {
        this.scheduleFallbackAdvance();
        return;
      }

      this.runProgressAnimation();
    }

    pause() {
      this.isPaused = true;
      this.clearFallbackTimer();
      if (this.trackFill) {
        this.trackFill.style.animationPlayState = 'paused';
      }
    }

    resume() {
      if (!this.isPaused || this.count < 2 || !this.isVisible) return;

      this.isPaused = false;

      if (this.reducedMotion) {
        this.scheduleFallbackAdvance();
        return;
      }

      if (this.trackFill?.classList.contains('is-animating')) {
        this.trackFill.style.animationPlayState = 'running';
        return;
      }

      this.runProgressAnimation();
    }
  }

  customElements.define('material-hero-v2', MaterialHeroV2);
}
